'use client'

import { AddComment, Close, CloseFullscreen, History, Link as LinkIcon, OpenInFull } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import type { FileUIPart } from 'ai'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { useGetConversationQuery, useShareConversationMutation } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'
import { useSnackbarControls } from '~/store/slices/display'

import ConversationList from './ConversationList'
import ConversationThread from './ConversationThread'
import NewConversation from './NewConversation'
import ShareLinkDialog from './ShareLinkDialog'
import type { ComposedMessage } from './contextRefs'
import { conversationLink } from './conversationLink'

type ActiveConversation = {
  id: string
  /** Set for a conversation just created from the empty state, whose first message is still to be sent. */
  initialPrompt?: ComposedMessage
  /** Files attached to that first message, uploaded to the conversation before it was sent. */
  initialFiles?: FileUIPart[]
}

type AssistantPanelProps = {
  organizationId: string
  /** A conversation a share link names; the panel opens it and reports back so the link is consumed. */
  linkedConversationId?: string
  onLinkedConversationOpened: () => void
  onClose: () => void
  /** Whether the panel fills the viewport. */
  isExpanded?: boolean
  /** Offers the control that widens the panel to the viewport and back. */
  onToggleExpanded?: () => void
}

/** The assistant's panel: a header, then either the conversation history or one conversation. */
export default function AssistantPanel({
  organizationId,
  linkedConversationId,
  onLinkedConversationOpened,
  onClose,
  isExpanded = false,
  onToggleExpanded
}: AssistantPanelProps) {
  const t = useTranslations('features.AssistantV2')
  const tBar = useTranslations('features.AssistantBar')
  const { openSnackbar } = useSnackbarControls()
  const [shareConversation, { isLoading: isSharing }] = useShareConversationMutation()
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [active, setActive] = useState<ActiveConversation | null>(null)

  useEffect(() => {
    if (!linkedConversationId) return
    setActive({ id: linkedConversationId })
    setIsHistoryOpen(false)
    onLinkedConversationOpened()
  }, [linkedConversationId, onLinkedConversationOpened])

  // Reads the entry the open thread already loaded; only the owner of a conversation may share it.
  const isNewConversation = active?.initialPrompt !== undefined
  const { isOwnedByViewer } = useGetConversationQuery(
    { conversationId: active?.id ?? '' },
    {
      skip: !active || isNewConversation,
      selectFromResult: ({ data }) => ({ isOwnedByViewer: data?.readOnly === false })
    }
  )
  // A conversation started in this panel belongs to the viewer; any other is shareable once its detail says so.
  const canShare = !!active && (isNewConversation || isOwnedByViewer)

  const [uncopiedLink, setUncopiedLink] = useState<string>()

  const openConversation = (conversation: ActiveConversation | null) => {
    setActive(conversation)
    setIsHistoryOpen(false)
  }

  const share = async () => {
    if (!active) return
    try {
      await shareConversation({
        conversationId: active.id,
        conversationShareParams: { shareScope: 'organization' }
      }).unwrap()
    } catch (error) {
      logger.error('Failed to share an assistant conversation', error)
      openSnackbar(t('shareError'), { severity: 'error' })
      return
    }
    const link = conversationLink(active.id)
    try {
      await navigator.clipboard.writeText(link)
      openSnackbar(t('shareCopied'), { severity: 'success' })
    } catch {
      // The conversation is shared either way; without clipboard access (an insecure context or a denied
      // permission) the link is shown to copy by hand.
      setUncopiedLink(link)
    }
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          {isHistoryOpen ? t('history') : t('title')}
        </Typography>
        <Tooltip title={t('history')}>
          <IconButton
            size="small"
            aria-label={t('history')}
            aria-pressed={isHistoryOpen}
            onClick={() => setIsHistoryOpen((value) => !value)}
          >
            <History fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('newChat')}>
          <IconButton size="small" aria-label={t('newChat')} onClick={() => openConversation(null)}>
            <AddComment fontSize="small" />
          </IconButton>
        </Tooltip>
        {canShare && (
          <Tooltip title={t('share')}>
            <span>
              <IconButton size="small" aria-label={t('share')} disabled={isSharing} onClick={() => void share()}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onToggleExpanded && (
          <Tooltip title={isExpanded ? tBar('collapseTooltip') : tBar('expandTooltip')}>
            <IconButton
              size="small"
              aria-label={isExpanded ? tBar('collapseAssistant') : tBar('expandAssistant')}
              aria-pressed={isExpanded}
              onClick={onToggleExpanded}
            >
              {isExpanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('close')}>
          <IconButton size="small" aria-label={t('close')} onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <PanelBody
        organizationId={organizationId}
        active={active}
        isHistoryOpen={isHistoryOpen}
        onOpenConversation={openConversation}
        onConversationDeleted={(id) => {
          // The open thread would otherwise keep showing, and posting to, the deleted conversation.
          setActive((current) => (current?.id === id ? null : current))
        }}
      />
      <ShareLinkDialog link={uncopiedLink} onClose={() => setUncopiedLink(undefined)} />
    </Stack>
  )
}

type PanelBodyProps = {
  organizationId: string
  active: ActiveConversation | null
  isHistoryOpen: boolean
  onOpenConversation: (conversation: ActiveConversation) => void
  onConversationDeleted: (conversationId: string) => void
}

function PanelBody({
  organizationId,
  active,
  isHistoryOpen,
  onOpenConversation,
  onConversationDeleted
}: PanelBodyProps) {
  // The conversation stays mounted under the history list so a reply keeps streaming while the list is open.
  return (
    <>
      {isHistoryOpen && (
        <ConversationList
          activeConversationId={active?.id}
          onSelect={(id) => onOpenConversation({ id })}
          onDeleted={onConversationDeleted}
        />
      )}
      <Stack sx={{ flex: 1, minHeight: 0, display: isHistoryOpen ? 'none' : 'flex' }}>
        {active ? (
          <ConversationThread
            key={active.id}
            conversationId={active.id}
            organizationId={organizationId}
            initialPrompt={active.initialPrompt}
            initialFiles={active.initialFiles}
          />
        ) : (
          <NewConversation
            onCreated={(id, initialPrompt, initialFiles) => onOpenConversation({ id, initialPrompt, initialFiles })}
          />
        )}
      </Stack>
    </>
  )
}
